import { isKnownTourTargetKey } from '../config/tourTargetCatalog'
import type {
  InvoiceTourStepAlign,
  InvoiceTourStepSide,
  InvoiceVirtualTour,
  InvoiceVirtualTourStepDefinition,
} from '../types/virtualTour.types'

export const CONTENTFUL_TOUR_STATUS_ACTIVE = 'active'
export const CONTENTFUL_TOUR_ENTRY_POINT_HELP_LAUNCHER = 'helpLauncher'

export interface LinkedAppEntry {
  fields?: {
    slug?: string
  }
}

export interface TourTargetEntry {
  fields?: {
    fordeerApp?: LinkedAppEntry
    key?: string
  }
}

export interface TourStepEntry {
  sys: {
    id: string
  }
  fields?: {
    align?: InvoiceTourStepAlign
    description?: string
    side?: InvoiceTourStepSide
    targetKey?: TourTargetEntry
    title?: string
  }
}

export interface TourEntry {
  sys: {
    id: string
  }
  fields?: {
    entryPointKey?: string
    fordeerApp?: LinkedAppEntry
    launcherLabel?: string
    launcherOrder?: number
    slug?: string
    status?: string
    steps?: TourStepEntry[]
    title?: string
  }
}

export const normalizeContentfulValue = (value?: string): string =>
  value?.trim().toLowerCase().replace(/[^a-z0-9]/g, '') ?? ''

/** Matches Contentful `fordeerApp` slug and @fordeer/ui-components `FordeerApp.Invoice`. */
const INVOICE_APP_SLUG_ALIASES = new Set([normalizeContentfulValue('invoice')])

export const matchesInvoiceApp = (linkedApp?: LinkedAppEntry): boolean => {
  const slug = normalizeContentfulValue(linkedApp?.fields?.slug)
  return INVOICE_APP_SLUG_ALIASES.has(slug)
}

const isNonNullable = <T,>(value: T | null | undefined): value is T => value != null

export const mapTourStep = (step: TourStepEntry): InvoiceVirtualTourStepDefinition | null => {
  const title = step.fields?.title
  const description = step.fields?.description
  const targetKey = step.fields?.targetKey?.fields?.key

  if (!title || !description || !targetKey) {
    return null
  }

  if (!matchesInvoiceApp(step.fields?.targetKey?.fields?.fordeerApp)) {
    return null
  }

  if (!isKnownTourTargetKey(targetKey)) {
    return null
  }

  return {
    title,
    description,
    targetKey,
    side: step.fields?.side,
    align: step.fields?.align,
  }
}

export const mapTour = (tour: TourEntry): InvoiceVirtualTour | null => {
  const fields = tour.fields

  if (!fields?.slug || !matchesInvoiceApp(fields.fordeerApp)) {
    return null
  }

  if (
    fields.status !== CONTENTFUL_TOUR_STATUS_ACTIVE ||
    fields.entryPointKey !== CONTENTFUL_TOUR_ENTRY_POINT_HELP_LAUNCHER
  ) {
    return null
  }

  const stepDefinitions = (fields.steps ?? []).map(mapTourStep).filter(isNonNullable)

  if (!stepDefinitions.length) {
    return null
  }

  return {
    id: tour.sys.id,
    slug: fields.slug,
    label: fields.launcherLabel || fields.title || 'Virtual tour',
    order: fields.launcherOrder ?? Number.MAX_SAFE_INTEGER,
    stepDefinitions,
  }
}

export const sortVirtualTours = (tours: InvoiceVirtualTour[]): InvoiceVirtualTour[] =>
  [...tours].sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order
    }

    return left.label.localeCompare(right.label)
  })
